import { useState } from "react";

export interface FilterValues {
  genre: string;
  region: string;
  year: string;
  sort: string;
}

interface SortOption {
  value: string;
  label: string;
}

interface FilterBarProps {
  genreOptions: string[];
  regionOptions: string[];
  yearOptions: number[];
  values: FilterValues;
  onChange: (patch: Partial<FilterValues>) => void;
  onReset: () => void;
  sortOptions?: SortOption[];
  resultCount: number;
  showSort?: boolean;
}

const selectClass =
  "w-full cursor-pointer rounded-md border border-background-200/70 bg-background-100 px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30";

const labelClass = "mb-1.5 block text-xs font-medium text-foreground-500";

export default function FilterBar({
  genreOptions,
  regionOptions,
  yearOptions,
  values,
  onChange,
  onReset,
  sortOptions,
  resultCount,
  showSort,
}: FilterBarProps) {
  const [expanded, setExpanded] = useState(false);

  const options: SortOption[] = sortOptions ?? [
    { value: "default", label: "默认排序" },
    { value: "newest", label: "最新上映" },
    { value: "hot", label: "热门程度" },
  ];

  const hasFilter = Boolean(values.genre || values.region || values.year);

  return (
    <section className="rounded-lg border border-background-200/60 bg-background-100/50 p-4 md:p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-foreground-600">
          <i className="ri-filter-3-line text-primary-500"></i>
          共找到 <span className="font-semibold text-foreground-950">{resultCount}</span> 部影片
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 rounded-md border border-background-200/70 px-3 py-1.5 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-500 md:hidden"
        >
          <i className="ri-equalizer-line"></i>
          筛选
        </button>
      </div>

      <div className={`${expanded ? "block" : "hidden"} mt-4 md:block`}>
        <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${showSort ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
          <div>
            <label className={labelClass}>类型</label>
            <select
              className={selectClass}
              value={values.genre}
              onChange={(e) => onChange({ genre: e.target.value })}
            >
              <option value="">全部类型</option>
              {genreOptions.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>地区</label>
            <select
              className={selectClass}
              value={values.region}
              onChange={(e) => onChange({ region: e.target.value })}
            >
              <option value="">全部地区</option>
              {regionOptions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>年份</label>
            <select
              className={selectClass}
              value={values.year}
              onChange={(e) => onChange({ year: e.target.value })}
            >
              <option value="">全部年份</option>
              {yearOptions.map((y) => (
                <option key={y} value={String(y)}>
                  {y} 年
                </option>
              ))}
            </select>
          </div>

          {showSort && (
            <div>
              <label className={labelClass}>排序</label>
              <select
                className={selectClass}
                value={values.sort}
                onChange={(e) => onChange({ sort: e.target.value })}
              >
                {options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {hasFilter && (
          <button
            type="button"
            onClick={onReset}
            className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-background-200/70 px-3 py-1.5 text-sm text-foreground-600 hover:border-primary-400 hover:text-primary-500"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        )}
      </div>
    </section>
  );
}